import { z } from "zod";
import { AccountIdSchema, AmoContactIdSchema, AmoLeadIdSchema, AmoUserIdSchema, AuditEventIdSchema, CourseTemplateIdSchema, CourseTemplateVersionIdSchema, EnrollmentIdSchema, InstructorIdSchema, LessonIdSchema, OutboxItemIdSchema, PaymentReportIdSchema, VenueIdSchema, } from "./ids.js";
const utcDateTime = z.string().datetime({ offset: true });
export const RoleSchema = z.enum(["instructor", "manager", "admin"]);
export const LessonStateSchema = z.enum([
    "scheduled",
    "client_arrived",
    "completed",
    "canceled",
    "no_show",
]);
export const AccountSchema = z.object({
    id: AccountIdSchema,
    amoAccountId: z.string().min(1),
    subdomain: z.string().min(1),
});
export const AmoEntitySnapshotSchema = z.object({
    accountId: AccountIdSchema,
    leadId: AmoLeadIdSchema,
    contactId: AmoContactIdSchema.nullable(),
    capturedAt: utcDateTime,
    payload: z.record(z.string(), z.unknown()),
});
export const InstructorSchema = z.object({
    id: InstructorIdSchema,
    accountId: AccountIdSchema,
    amoCatalogElementId: z.string().min(1),
    amoUserId: AmoUserIdSchema.nullable(),
    displayName: z.string().trim().min(1),
    active: z.boolean(),
});
export const VenueSchema = z.object({
    id: VenueIdSchema,
    accountId: AccountIdSchema,
    name: z.string().trim().min(1),
    capacity: z.number().int().positive(),
    timeZone: z.string().trim().min(1),
    active: z.boolean(),
});
export const CourseTemplateSchema = z.object({
    id: CourseTemplateIdSchema,
    accountId: AccountIdSchema,
    code: z.string().trim().min(1),
    name: z.string().trim().min(1),
});
export const CourseTemplateVersionSchema = z.object({
    id: CourseTemplateVersionIdSchema,
    templateId: CourseTemplateIdSchema,
    version: z.number().int().positive(),
    kind: z.enum(["fixed", "open_ended"]),
    lessonCount: z.number().int().positive().nullable(),
    createdAt: utcDateTime,
});
export const EnrollmentSchema = z.object({
    id: EnrollmentIdSchema,
    accountId: AccountIdSchema,
    leadId: AmoLeadIdSchema,
    contactId: AmoContactIdSchema.nullable(),
    templateVersionId: CourseTemplateVersionIdSchema,
    primaryInstructorId: InstructorIdSchema,
    state: z.enum(["active", "completed", "terminated"]),
    createdAt: utcDateTime,
});
export const LessonSchema = z.object({
    id: LessonIdSchema,
    enrollmentId: EnrollmentIdSchema,
    ordinal: z.number().int().positive(),
    instructorId: InstructorIdSchema,
    venueId: VenueIdSchema,
    state: LessonStateSchema,
    startsAt: utcDateTime,
    durationMinutes: z.number().int().positive(),
    version: z.number().int().nonnegative(),
});
export const PaymentReportSchema = z.object({
    id: PaymentReportIdSchema,
    lessonId: LessonIdSchema,
    state: z.enum(["pending_manager_review", "confirmed", "rejected"]),
    method: z.enum(["cash", "transfer", "usdt", "already_paid"]),
    comment: z.string().max(2_000).nullable(),
    createdAt: utcDateTime,
});
export const AuditEventSchema = z.object({
    id: AuditEventIdSchema,
    aggregateType: z.enum(["enrollment", "lesson", "payment_report"]),
    aggregateId: z.string().min(1),
    actorId: z.string().min(1),
    role: RoleSchema,
    occurredAt: utcDateTime,
    priorVersion: z.number().int().nonnegative(),
    correlationKey: z.string().min(1),
    before: z.unknown(),
    after: z.unknown(),
});
export const OutboxItemSchema = z.object({
    id: OutboxItemIdSchema,
    topic: z.string().min(1),
    idempotencyKey: z.string().min(1),
    payload: z.record(z.string(), z.unknown()),
    state: z.enum(["pending", "processing", "delivered", "failed"]),
    attempts: z.number().int().nonnegative(),
    availableAt: utcDateTime,
});
